import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Search, Clock, Scale, CheckCircle2, TrendingUp, Zap, Activity, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { listEscrowInvoices } from "@/lib/escrow.functions";
import { EscrowCard, type EscrowInvoice } from "@/components/escrow/EscrowCard";
import { useAuth } from "@/hooks/use-auth";
import { useCountUp } from "@/hooks/use-count-up";

export const Route = createFileRoute("/_authenticated/escrow")({
  head: () => ({ meta: [{ title: "Payment Protection · Lumvio" }] }),
  errorComponent: ({ error }) => (
    <div className="p-6 text-sm text-destructive">{String(error?.message ?? error)}</div>
  ),
  notFoundComponent: () => <div className="p-6">—</div>,
  component: EscrowPage,
});

type Filter = "all" | "in_escrow" | "disputed" | "released";

function EscrowPage() {
  const { t, i18n } = useTranslation();
  const { user, profile } = useAuth();
  const fetchEscrow = useServerFn(listEscrowInvoices);

  useEffect(() => {
    if (typeof document !== "undefined") document.title = t("escrow.pageTitle");
  }, [t, i18n.language]);

  const { data, isLoading } = useQuery({
    queryKey: ["escrow", user?.id],
    queryFn: async () => (await fetchEscrow()).invoices as unknown as EscrowInvoice[],
    enabled: !!user?.id,
    refetchInterval: 60_000,
  });

  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");

  const enriched = useMemo(() => {
    const rows = data ?? [];
    return rows
      .map((r) => {
        const state: Filter =
          r.dispute_status === "open"
            ? "disputed"
            : r.status === "released_to_freelancer"
              ? "released"
              : "in_escrow";
        return { row: r, state };
      })
      .filter((x) => (filter === "all" ? true : x.state === filter))
      .filter((x) =>
        !q.trim()
          ? true
          : (x.row.projects?.title ?? "").toLowerCase().includes(q.toLowerCase()) ||
            (x.row.description ?? "").toLowerCase().includes(q.toLowerCase()),
      );
  }, [data, filter, q]);

  const totals = useMemo(() => {
    const rows = data ?? [];
    let held = 0, disputed = 0, released = 0, nextRelease: string | null = null;
    const releaseDurations: number[] = [];
    let inEscrowCount = 0, disputedCount = 0, releasedCount = 0;
    for (const r of rows) {
      const net = Number(r.net_amount);
      if (r.dispute_status === "open") { disputed += net; disputedCount++; }
      else if (r.status === "released_to_freelancer") {
        released += net; releasedCount++;
        if (r.paid_at && r.released_at) {
          releaseDurations.push(new Date(r.released_at).getTime() - new Date(r.paid_at).getTime());
        }
      }
      else if (r.status === "paid_to_platform") {
        held += net - Number(r.partial_released_amount ?? 0);
        inEscrowCount++;
        if (r.auto_release_at) {
          if (!nextRelease || new Date(r.auto_release_at) < new Date(nextRelease)) {
            nextRelease = r.auto_release_at;
          }
        }
      }
    }
    const total = rows.length || 1;
    const successRate = Math.round((releasedCount / total) * 100);
    const avgReleaseHours = releaseDurations.length
      ? releaseDurations.reduce((a, b) => a + b, 0) / releaseDurations.length / 3_600_000
      : 0;
    return { held, disputed, released, nextRelease, inEscrowCount, disputedCount, releasedCount, total: rows.length, successRate, avgReleaseHours };
  }, [data]);

  const role: "client" | "freelancer" | "admin" = profile?.role === "freelancer" ? "freelancer" : "client";

  const countByFilter: Record<Filter, number> = {
    all: totals.total,
    in_escrow: totals.inEscrowCount,
    disputed: totals.disputedCount,
    released: totals.releasedCount,
  };

  const filterOptions: { key: Filter; label: string }[] = [
    { key: "all", label: t("escrow.filters.all") },
    { key: "in_escrow", label: t("escrow.filters.inEscrow") },
    { key: "disputed", label: t("escrow.filters.disputed") },
    { key: "released", label: t("escrow.filters.released") },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-10 p-4 sm:p-8">
      {/* ───── Hero (Apple-style: quiet, precise, monochrome) ───── */}
      <section className="animate-fade-in">
        <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500/60" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
          <span>{t("escrow.badge")}</span>
        </div>
        <h1 className="mt-3 text-4xl font-semibold tracking-[-0.03em] text-foreground sm:text-5xl">
          {t("escrow.title")}
        </h1>
        <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-muted-foreground">
          {t("escrow.subtitle")}
        </p>
      </section>

      {/* ───── Balance strip (marquee card + supporting tiles) ───── */}
      <section className="grid gap-4 lg:grid-cols-3">
        {/* Primary balance — matte tile with specular top highlight */}
        <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-b from-card to-card/60 p-6 shadow-[0_1px_0_hsl(0_0%_100%/0.06)_inset,0_20px_60px_-30px_rgb(0_0_0/0.5)] lg:col-span-2">
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
          <div aria-hidden className="pointer-events-none absolute -top-32 -right-24 h-72 w-72 rounded-full bg-emerald-500/[0.06] blur-3xl" />

          <div className="relative flex items-start justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                <ShieldCheck className="h-3 w-3" /> {t("escrow.stats.held")}
              </div>
              <BigAmount value={totals.held} />
              {totals.nextRelease ? (
                <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/60 px-3 py-1.5 text-xs text-muted-foreground backdrop-blur">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{t("escrow.nextRelease")}</span>
                  <span className="font-medium tabular-nums text-foreground">
                    {new Date(totals.nextRelease).toLocaleString(i18n.language === "ar" ? "ar" : "en", {
                      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                    })}
                  </span>
                </div>
              ) : null}
            </div>

            {/* Precision ring: success rate */}
            <div className="hidden sm:block">
              <ProgressRing value={totals.successRate} />
            </div>
          </div>
        </div>

        {/* Supporting tiles */}
        <div className="grid gap-4">
          <MiniStat
            icon={<Scale className="h-3.5 w-3.5" />}
            label={t("escrow.stats.disputed")}
            value={totals.disputed}
            tone="destructive"
          />
          <MiniStat
            icon={<CheckCircle2 className="h-3.5 w-3.5" />}
            label={t("escrow.stats.released")}
            value={totals.released}
            tone="success"
          />
        </div>
      </section>

      {/* ───── Insights (only when there's data) ───── */}
      {totals.total > 0 ? (
        <section className="grid gap-3 sm:grid-cols-3">
          <InsightTile
            icon={<TrendingUp className="h-4 w-4" />}
            label={t("escrow.insights.successRate", { defaultValue: "Success rate" })}
            value={`${totals.successRate}%`}
            progress={totals.successRate}
            tone="success"
          />
          <InsightTile
            icon={<Zap className="h-4 w-4" />}
            label={t("escrow.insights.avgRelease", { defaultValue: "Avg release time" })}
            value={
              totals.avgReleaseHours > 0
                ? totals.avgReleaseHours >= 24
                  ? `${(totals.avgReleaseHours / 24).toFixed(1)}d`
                  : `${totals.avgReleaseHours.toFixed(1)}h`
                : "—"
            }
            tone="primary"
          />
          <InsightTile
            icon={<Activity className="h-4 w-4" />}
            label={t("escrow.insights.active", { defaultValue: "Active protected payments" })}
            value={String(totals.inEscrowCount)}
            tone="primary"
            pulse={totals.inEscrowCount > 0}
          />
        </section>
      ) : null}

      {/* ───── Toolbar (segmented + search) ───── */}
      <div className="sticky top-2 z-20 flex flex-col gap-3 rounded-2xl border border-border/60 bg-background/70 p-2 backdrop-blur-xl supports-[backdrop-filter]:bg-background/50 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex flex-wrap items-center gap-1 rounded-full border border-border/60 bg-card/60 p-1">
          {filterOptions.map((f) => {
            const isActive = filter === f.key;
            const count = countByFilter[f.key];
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`group relative inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-foreground text-background shadow-[0_1px_0_hsl(0_0%_100%/0.15)_inset,0_1px_2px_rgb(0_0_0/0.2)]"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {f.label}
                <span
                  className={`inline-flex min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] tabular-nums transition ${
                    isActive ? "bg-background/20 text-background" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("escrow.searchPlaceholder")}
            className="rounded-full ps-9"
          />
        </div>
      </div>

      {/* ───── List ───── */}
      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-40 animate-pulse rounded-2xl border border-border/60 bg-card/40" />
          ))}
        </div>
      ) : enriched.length === 0 ? (
        <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-b from-card to-card/60 p-14 text-center">
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          <div className="relative mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-border/60 bg-background/60 text-foreground">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div className="relative mt-5 text-lg font-semibold tracking-tight">{t("escrow.emptyTitle")}</div>
          <div className="relative mx-auto mt-2 max-w-sm text-sm text-muted-foreground">{t("escrow.emptyBody")}</div>
        </div>
      ) : (
        <div className="space-y-4">
          {enriched.map(({ row }, i) => (
            <div
              key={row.id}
              className="animate-fade-in transition-transform duration-300 hover:-translate-y-0.5"
              style={{ animationDelay: `${Math.min(i, 8) * 60}ms`, animationFillMode: "both" }}
            >
              <EscrowCard invoice={row} role={role} />
            </div>
          ))}
        </div>
      )}

      <p className="pt-2 text-center text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        <Clock className="me-1.5 inline h-3 w-3" /> {t("escrow.liveUpdate")}
      </p>
    </div>
  );
}

/* ─────────── Sub-components ─────────── */

function BigAmount({ value }: { value: number }) {
  const animated = useCountUp(value);
  return (
    <div className="mt-2 flex items-baseline gap-1.5">
      <span className="text-sm font-medium text-muted-foreground">$</span>
      <span className="text-5xl font-semibold tracking-[-0.04em] text-foreground tabular-nums sm:text-6xl">
        {animated.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </div>
  );
}

function ProgressRing({ value }: { value: number }) {
  const { t } = useTranslation();
  const successLabel = t("escrow.insights.successLabel");
  const clamped = Math.max(0, Math.min(100, value));
  const r = 42;
  const c = 2 * Math.PI * r;
  const offset = c - (clamped / 100) * c;
  return (
    <div className="relative flex h-28 w-28 items-center justify-center">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
        <circle cx="50" cy="50" r={r} className="fill-none stroke-border/60" strokeWidth="6" />
        <circle
          cx="50" cy="50" r={r}
          className="fill-none stroke-emerald-500 transition-[stroke-dashoffset] duration-1000 ease-out"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-lg font-semibold tabular-nums tracking-tight">{clamped}%</div>
        <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
          {successLabel}
        </div>
      </div>
    </div>
  );
}

function MiniStat({
  icon, label, value, tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone: "success" | "destructive";
}) {
  const animated = useCountUp(value);
  const dot = tone === "success" ? "bg-emerald-500" : "bg-destructive";
  const num = tone === "success" ? "text-emerald-600 dark:text-emerald-400" : "text-destructive";
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/60 p-4 shadow-[0_1px_0_hsl(0_0%_100%/0.05)_inset] transition-all duration-300 hover:-translate-y-0.5 hover:border-border">
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
          {icon} {label}
        </div>
        <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
      <div className={`mt-1.5 text-2xl font-semibold tracking-tight tabular-nums ${num}`}>
        ${animated.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}

function InsightTile({
  icon, label, value, progress, tone = "primary", pulse = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  progress?: number;
  tone?: "primary" | "success" | "destructive";
  pulse?: boolean;
}) {
  const iconBg =
    tone === "success" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : tone === "destructive" ? "bg-destructive/10 text-destructive"
      : "bg-foreground/5 text-foreground";
  const bar =
    tone === "success" ? "bg-emerald-500"
      : tone === "destructive" ? "bg-destructive"
      : "bg-foreground";
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/60 p-4 shadow-[0_1px_0_hsl(0_0%_100%/0.05)_inset] transition-all duration-300 hover:-translate-y-0.5">
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
        <div className={`relative flex h-8 w-8 items-center justify-center rounded-full ${iconBg}`}>
          {pulse ? <span aria-hidden className="absolute inset-0 animate-ping rounded-full bg-current opacity-20" /> : null}
          <span className="relative">{icon}</span>
        </div>
      </div>
      <div className="mt-2 text-2xl font-semibold tracking-tight tabular-nums text-foreground">{value}</div>
      {typeof progress === "number" ? (
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-muted/60">
          <div
            className={`h-full rounded-full transition-all duration-1000 ${bar}`}
            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}
